import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Lock, Play, Radio } from "lucide-react";
import { Spinner } from "@/components/catalog/TitleGrid";
import { api } from "@/lib/api";

export const useCountdown = (iso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const ms = Math.max(0, new Date(iso).getTime() - now);
  return { done: ms === 0, d: Math.floor(ms / 864e5), h: Math.floor(ms / 36e5) % 24, m: Math.floor(ms / 6e4) % 60, s: Math.floor(ms / 1e3) % 60 };
};

export const Countdown = ({ iso, testId = "countdown" }) => {
  const c = useCountdown(iso);
  return (
    <div data-testid={testId} className="flex gap-2 sm:gap-3">
      {[["d", "Days"], ["h", "Hrs"], ["m", "Min"], ["s", "Sec"]].map(([k, l]) => (
        <div key={k} className="min-w-[58px] sm:min-w-[72px] rounded-xl border border-[#D4AF37]/40 bg-black/60 px-2 py-2 text-center backdrop-blur">
          <p className="font-display text-2xl sm:text-3xl font-bold gold-text tabular-nums">{String(c[k]).padStart(2, "0")}</p>
          <p className="text-[0.6rem] uppercase tracking-[0.2em] text-[#8B8B8B]">{l}</p>
        </div>
      ))}
    </div>
  );
};

export const LiveBadge = ({ status, testId }) => (status === "live"
  ? <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full bg-[#E50914] px-2.5 py-1 text-[0.65rem] font-extrabold tracking-[0.2em] text-white shadow-[0_0_20px_rgba(229,9,20,0.6)]"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> LIVE NOW</span>
  : status === "ended" ? <span data-testid={testId} className="rounded-full border border-white/25 px-2.5 py-1 text-[0.65rem] font-bold tracking-[0.2em] text-white/70">REPLAY</span>
    : <span data-testid={testId} className="rounded-full border border-[#D4AF37]/60 px-2.5 py-1 text-[0.65rem] font-bold tracking-[0.2em] text-[#FFD86A]">UPCOMING</span>);

const TIER = { free: "Free", premium: "Premium", studio_early: "Studio early access" };
export const fmtWhen = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const EventCard = ({ e }) => (
  <Link to={`/live-events/${e.slug}`} data-testid={`event-card-${e.slug}`} className="group relative block overflow-hidden rounded-xl border border-[#D4AF37]/20 bg-[#0B0B0D] transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/60">
    <div className="relative aspect-video"><img src={e.banner_url} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /><div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
      <div className="absolute left-3 top-3"><LiveBadge status={e.status} /></div>
      {e.locked_reason && <Lock className="absolute right-3 top-3 h-7 w-7 rounded-full border border-[#FFD86A]/60 bg-black/70 p-1.5 text-[#FFD86A]" />}
    </div>
    <div className="p-4">
      <p className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{e.type} · {TIER[e.access_tier]}</p>
      <p className="mt-1 font-display text-lg font-bold text-white line-clamp-1">{e.title}</p>
      <p className="mt-1 flex items-center gap-1.5 text-xs text-[#8B8B8B]"><CalendarClock className="h-3.5 w-3.5" /> {fmtWhen(e.start_at)}</p>
    </div>
  </Link>
);

const NextPremiere = ({ e }) => {
  const c = useCountdown(e.start_at);
  return (
    <Link to={`/live-events/${e.slug}`} data-testid="live-hero-next-premiere" className="mt-6 inline-flex max-w-full items-center gap-3 rounded-full border border-[#D4AF37]/40 bg-black/60 px-4 py-2 text-xs backdrop-blur transition-colors hover:border-[#FFD86A]">
      <CalendarClock className="h-4 w-4 flex-none text-[#D4AF37]" />
      <span className="truncate text-white/80">Next premiere in <span data-testid="next-premiere-countdown" className="font-bold tabular-nums text-[#FFD86A]">{c.d}d {String(c.h).padStart(2, "0")}h {String(c.m).padStart(2, "0")}m {String(c.s).padStart(2, "0")}s</span> · {e.title}</span>
    </Link>
  );
};

const Hero = ({ e, next }) => (
  <section data-testid="live-hero" className="relative min-h-[78vh] overflow-hidden">
    <img src={e.banner_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" /><div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050505] to-transparent" />
    <div className="relative z-10 flex min-h-[78vh] max-w-3xl flex-col justify-end px-4 sm:px-8 lg:px-14 pb-16 pt-32">
      <div className="flex items-center gap-3"><LiveBadge status={e.status} testId="live-hero-badge" /><span className="text-xs font-bold uppercase tracking-[0.25em] text-[#D4AF37]">{e.type}</span></div>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold gold-text">{e.title}</h1>
      <p className="mt-4 max-w-xl text-sm sm:text-base text-white/75">{e.description}</p>
      <div className="mt-6">{e.status === "upcoming" ? <Countdown iso={e.start_at} testId="live-hero-countdown" /> : null}</div>
      <div className="mt-7 flex flex-wrap gap-3">
        {e.status === "live" && <Link to={`/live-events/${e.slug}/watch`} data-testid="live-hero-watch" className="btn-gold px-7 py-3 text-sm"><Radio className="h-5 w-5" /> Watch live</Link>}
        <Link to={`/live-events/${e.slug}`} data-testid="live-hero-details" className="btn-ghost-gold px-7 py-3 text-sm"><Play className="h-4 w-4" /> Event details</Link>
      </div>
      {e.status === "live" && next && <NextPremiere e={next} />}
    </div>
  </section>
);

export default function LiveEvents() {
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get("/live/events").then((r) => setEvents(r.data)).catch(() => setEvents([])); }, []);
  if (!events) return <div className="pt-32"><Spinner /></div>;
  const live = events.filter((e) => e.status === "live");
  const upcoming = events.filter((e) => e.status === "upcoming").sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  const replays = events.filter((e) => e.status === "ended");
  const hero = live[0] || upcoming[0] || replays[0];
  const next = upcoming.find((e) => e.type === "Global Premiere") || upcoming[0];
  return (
    <div data-testid="live-events-page" className="pb-16">
      {hero ? <Hero e={hero} next={next} /> : <p className="pt-40 text-center text-white/60">No events scheduled yet.</p>}
      {[["Live now", live, "live-now-grid"], ["Upcoming", upcoming, "upcoming-grid"], ["Replays", replays, "replays-rail"]].map(([label, list, id]) => list.length > 0 && (
        <section key={id} className="mt-10 px-4 sm:px-8 lg:px-14">
          <h2 className="mb-4 font-display text-base md:text-lg uppercase tracking-[0.28em] gold-text">{label}</h2>
          <div data-testid={id} className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{list.map((e) => <EventCard key={e.slug} e={e} />)}</div>
        </section>
      ))}
    </div>
  );
}
